/**
 * Environment configuration with sensible defaults
 * and support for different deployment environments
 */
export interface Environment {
  isLocalDevelopment: boolean;
  isPodmanTest: boolean;
  isProduction: boolean;
  isBrowser: boolean;

  // Storage configuration
  storageType: 'localStorage' | 'dynamoDB' | 'api';

  // AWS Configuration
  awsRegion?: string;
  dynamoDbEndpoint?: string;

  // API Configuration
  apiEndpoint?: string;

  // Redis Configuration
  redisHost?: string;
  redisPort?: number;
  redisUrl?: string;
}

// Augment Window interface to include our custom ENV property
declare global {
  interface Window {
    __ENV__?: Record<string, string>;
  }

  // Add proper type for Vite env variables
  interface ImportMeta {
    env: {
      VITE_STORAGE_TYPE?: 'localStorage' | 'dynamoDB' | 'api';
      VITE_DYNAMODB_ENDPOINT?: string;
      VITE_API_ENDPOINT?: string;
      VITE_REDIS_HOST?: string;
      VITE_REDIS_PORT?: string;
      VITE_REDIS_URL?: string;
      [key: string]: string | undefined;
    };
  }
}

/**
 * Get environment configuration based on current context
 */
export const getEnvironment = (): Environment => {
  // More reliable browser detection
  const isBrowser =
    typeof window !== 'undefined' && typeof process === 'undefined';

  // Safe access to process.env in any environment
  const getEnv = (key: string): string | undefined => {
    if (isBrowser && window.__ENV__) {
      return window.__ENV__[key];
    }
    if (!isBrowser && process?.env) {
      return process.env[key];
    }
    return undefined;
  };

  const nodeEnv = getEnv('NODE_ENV') || 'development';

  // Determine storage type based on environment variables
  let storageType: 'localStorage' | 'dynamoDB' | 'api' = 'localStorage';

  // Use Vite environment variables if available (for browser)
  if (isBrowser && typeof import.meta !== 'undefined') {
    const viteStorageType = import.meta.env.VITE_STORAGE_TYPE;
    if (viteStorageType === 'dynamoDB' || viteStorageType === 'api') {
      storageType = viteStorageType;
    }
  } else if (getEnv('STORAGE_TYPE')) {
    // For Node.js environments
    const envStorageType = getEnv('STORAGE_TYPE');
    if (envStorageType === 'dynamoDB' || envStorageType === 'api') {
      storageType = envStorageType as 'dynamoDB' | 'api';
    }
  }

  return {
    isLocalDevelopment: nodeEnv === 'development',
    isPodmanTest: getEnv('TEST_ENV') === 'podman',
    isProduction: nodeEnv === 'production',
    isBrowser,

    // Storage configuration
    storageType,

    // AWS Configuration
    awsRegion: getEnv('AWS_REGION'),
    dynamoDbEndpoint:
      getEnv('DYNAMODB_ENDPOINT') ||
      (isBrowser && typeof import.meta !== 'undefined'
        ? import.meta.env.VITE_DYNAMODB_ENDPOINT
        : undefined),

    // API Configuration
    apiEndpoint:
      getEnv('API_ENDPOINT') ||
      (isBrowser && typeof import.meta !== 'undefined'
        ? import.meta.env.VITE_API_ENDPOINT
        : undefined),

    // Redis Configuration
    redisHost:
      getEnv('REDIS_HOST') ||
      (isBrowser && typeof import.meta !== 'undefined'
        ? import.meta.env.VITE_REDIS_HOST
        : undefined),
    redisPort: getEnv('REDIS_PORT')
      ? parseInt(getEnv('REDIS_PORT') as string, 10)
      : isBrowser &&
          typeof import.meta !== 'undefined' &&
          import.meta.env.VITE_REDIS_PORT
        ? parseInt(import.meta.env.VITE_REDIS_PORT, 10)
        : undefined,
    redisUrl:
      getEnv('REDIS_URL') ||
      (isBrowser && typeof import.meta !== 'undefined'
        ? import.meta.env.VITE_REDIS_URL
        : undefined),
  };
};
